import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShoppingCart, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="login-split">
      {/* Left side - Login form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-8">
              <div className="h-9 w-9 bg-emerald-600 flex items-center justify-center">
                <ShoppingCart className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
                OrderGenie
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Welcome back
            </h1>
            <p className="mt-2 text-sm text-zinc-500">
              Sign in to manage your purchase orders
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="field-label">Username</label>
              <Input
                data-testid="login-email-input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Username"
                className="rounded-none border-zinc-300 focus:border-emerald-600 focus-visible:ring-0 h-11"
                required
              />
            </div>
            <div>
              <label className="field-label">Password</label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="rounded-none border-zinc-300 focus:border-emerald-600 focus-visible:ring-0 h-11"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-medium transition-colors"
            >
              {loading ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-xs text-zinc-400 text-center">
            Internal tool for authorized personnel only
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="login-image-side relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/bb0b9263-a37a-4e9f-8280-005b26def085/images/14bdb4c759713535b42543e6c210a6b76955736a428baa39dd5226f994573a52.png"
          alt="Fresh produce in a supermarket"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="text-white/90 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Smart ordering starts with clean data
          </p>
          <p className="text-white/60 text-sm mt-2">
            Upload sales data, get instant purchase recommendations
          </p>
        </div>
      </div>
    </div>
  );
}
